import { useState, type FormEvent } from 'react';

interface Comment {
  id: string;
  user: string;
  initial: string;
  time: string;
  content: string;
  likes: number;
}

const initialComments: Comment[] = [
  {
    id: 'c1',
    user: '光影拾荒者',
    initial: '光',
    time: '2 小时前',
    content: '国语对白很有味道，画面质感也在线，整体节奏把握得不错，值得一看。',
    likes: 128,
  },
  {
    id: 'c2',
    user: '晚风影评',
    initial: '晚',
    time: '昨天',
    content: '细节铺垫很足，二刷还能发现新的伏笔，推荐给喜欢慢慢品的朋友。',
    likes: 86,
  },
  {
    id: 'c3',
    user: '匿名观众',
    initial: '匿',
    time: '3 天前',
    content: '简介写得很清楚，找你想要的类型很方便，希望多更新一些同类资源。',
    likes: 43,
  },
];

export default function CommentSection() {
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) {
      setError('评论内容不能为空哦');
      return;
    }
    if (trimmed.length > 500) {
      setError('评论内容不能超过 500 字');
      return;
    }
    setError('');
    const displayName = name.trim() || '匿名观众';
    const newComment: Comment = {
      id: `c-${Date.now()}`,
      user: displayName,
      initial: displayName.slice(0, 1),
      time: '刚刚',
      content: trimmed,
      likes: 0,
    };
    setComments((prev) => [newComment, ...prev]);
    setName('');
    setContent('');
  };

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section className="mt-10">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-1 h-7 rounded-full bg-accent-500"></span>
        <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
          用户评论 <span className="text-base font-normal text-foreground-500">({comments.length})</span>
        </h2>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="昵称（选填）"
            className="h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 sm:w-48"
          />
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, 500))}
          rows={3}
          maxLength={500}
          placeholder="说点什么吧，友善交流哦～"
          className="mt-3 w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 resize-none"
        />
        <div className="mt-3 flex items-center justify-between">
          <span className={`text-xs ${error ? 'text-accent-600' : 'text-foreground-400'}`}>
            {error || `${content.length}/500 字（演示评论，仅本地展示）`}
          </span>
          <button
            type="submit"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-send-plane-line"></i>
            发表评论
          </button>
        </div>
      </form>

      <ul className="mt-5 space-y-3">
        {comments.map((c) => (
          <li key={c.id} className="flex gap-3 p-4 bg-background-50 border border-background-200 rounded-lg">
            <span className="w-10 h-10 shrink-0 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center font-semibold">
              {c.initial}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                <span className="text-sm font-semibold text-foreground-900">{c.user}</span>
                <span className="text-xs text-foreground-400">{c.time}</span>
              </div>
              <p className="mt-1.5 text-sm text-foreground-700 leading-relaxed">{c.content}</p>
              <button
                type="button"
                onClick={() => toggleLike(c.id)}
                className={`mt-2 inline-flex items-center gap-1 text-xs transition-colors cursor-pointer ${
                  liked[c.id] ? 'text-accent-600' : 'text-foreground-400 hover:text-accent-500'
                }`}
              >
                <i className={liked[c.id] ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                {c.likes + (liked[c.id] ? 1 : 0)}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}